import React, { useMemo, useState } from 'react';
import { useSettings } from '../../context/SettingsContext';
import aflPlayerData from '../../assets/afl_player_data_2026.json';
import DetailedPlayerVoteModal from './DetailedPlayerVoteModal';
import BreakdownIcon from '../icons/BreakdownIcon';
import { deadHeatShare, formatWins, formatPct } from '../../utils/deadHeat';
import ImpliedOddsInfo from '../common/ImpliedOddsInfo';
// Individual count sources
// Removed static individual count imports; use remote data only

const ROUNDS = Array.from({ length: 25 }, (_, i) => i); // 0..24

// Team maps are derived from remote data within component

const normalize = (value) =>
  (value || '')
    .toString()
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const PER_PLAYER_ROUND_TOTALS = new Map();

// Removed static hot streak precompute; computed dynamically below

// Helper to build per-player round totals map from a data set using provided team map
const buildPerPlayerRoundTotalsFrom = (dataSet, teamMap) => {
  const result = new Map();
  const data = dataSet && dataSet.game_vote_data ? dataSet.game_vote_data : [];
  for (const roundData of data) {
    const round = String(roundData.round);
    for (const game of roundData.game_votes) {
      for (const vote of game.votes) {
        const key = vote.player.toLowerCase();
        if (!teamMap || !teamMap.has(key)) continue;
        const expectedTeam = normalize(teamMap.get(key));
        if (normalize(vote.team) !== expectedTeam) continue;
        if (!result.has(key)) result.set(key, {});
        const r = result.get(key);
        r[round] = (r[round] || 0) + vote.votes;
      }
    }
  }
  return result;
};

// Hot streak marker — heroicons "fire" (solid), brand amber accent
const HotStreakIcon = ({ className = 'w-2.5 h-2.5' }) => (
  <svg className={`${className} text-amber-400`} viewBox="0 0 24 24" fill="currentColor" aria-label="Hot streak" role="img">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.963 2.286a.75.75 0 00-1.071-.136 9.742 9.742 0 00-3.539 6.176 7.547 7.547 0 01-1.705-1.715.75.75 0 00-1.152-.082A9 9 0 1015.68 4.534a7.46 7.46 0 01-2.717-2.248zM15.75 14.25a3.75 3.75 0 11-7.313-1.172c.628.465 1.35.81 2.133 1a5.99 5.99 0 011.925-3.545 3.75 3.75 0 013.255 3.717z"
    />
  </svg>
);

const PLAYER_NAME_TO_IMG = (() => {
  const map = new Map();
  for (const ap of aflPlayerData) {
    map.set(ap.player_name, ap.image_src);
  }
  return map;
})();

// Caret shown in a sortable header when that column is the active sort key.
const SortCaret = () => (
  <svg className="inline-block w-2.5 h-2.5 ml-1 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7" />
  </svg>
);

const ChevronDown = ({ className = 'w-3.5 h-3.5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
  </svg>
);

// Podium emphasis for the position column: emerald for the leader, a raised
// slate chip for the rest of the top three, bare numerals below that.
const rankBadgeClass = (rank) => {
  if (rank === 1) return 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300';
  if (rank === 2 || rank === 3) return 'bg-slate-800/80 border-slate-700/70 text-slate-200';
  return 'border-transparent text-slate-500';
};

// The frozen POS/PLAYER columns need an opaque surface so scrolled rounds cannot
// show through, and have to track the row hover tint themselves. PLAYER pins a
// couple of pixels under the POS column width so the two frozen cells always
// overlap into POS's trailing padding rather than leaving a seam to bleed through.
const STICKY_CELL = 'md:sticky md:z-10 bg-[#0a1120] group-hover/row:bg-[#121b2b] transition-colors';
const STICKY_HEAD = 'md:sticky md:z-20 bg-[#0e1625]';

const PlayerVoteSummaryTable = ({ searchTerm = '', filterFrom = null, filterTo = null, voteFrequencyEnabled = false, freqThresholds, selectedDataset = 'all', voteDataRemote = null }) => {
  const players = voteDataRemote?.leaderboard || [];
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sortKey, setSortKey] = useState('total');
  const [expandedMobileRows, setExpandedMobileRows] = useState({});
  const { getEnabledKeys } = useSettings();
  const enabledKeys = getEnabledKeys();
  const enabledSet = new Set(enabledKeys);

  // On mobile the summary rows default to expanded so the per-round strips are
  // visible immediately; users can still collapse individual rows. Each strip
  // scrolls independently.
  const toggleMobileRow = (playerName) => {
    setExpandedMobileRows((prev) => ({ ...prev, [playerName]: !(prev[playerName] ?? true) }));
  };

  // Build team map from current players (stable reference for useMemo)
  const teamMap = useMemo(() => {
    const map = new Map();
    for (const p of players) {
      map.set(p.name.toLowerCase(), (p.full_team_name || p.team || '').toString());
    }
    return map;
  }, [players]);

  // Build dynamic maps from remote data using current team map
  const DATASET_TOTALS = useMemo(() => {
    const wrap = (d) => (Array.isArray(d) ? { game_vote_data: d } : d);
    if (!voteDataRemote || teamMap.size === 0) return {
      all: new Map(), byb: new Map(), espn: new Map(), betfair: new Map(),
      count1: new Map(), count2: new Map(), count3: new Map(), count4: new Map(), count5: new Map(),
    };
    return {
      all: buildPerPlayerRoundTotalsFrom(wrap(voteDataRemote.game_vote_data), teamMap),
      byb: buildPerPlayerRoundTotalsFrom(wrap(voteDataRemote.game_vote_data_byb), teamMap),
      espn: buildPerPlayerRoundTotalsFrom(wrap(voteDataRemote.game_vote_data_espn), teamMap),
      betfair: buildPerPlayerRoundTotalsFrom(wrap(voteDataRemote.game_vote_data_betfair), teamMap),
      count1: buildPerPlayerRoundTotalsFrom(wrap(voteDataRemote.game_vote_data_count_1), teamMap),
      count2: buildPerPlayerRoundTotalsFrom(wrap(voteDataRemote.game_vote_data_count_2), teamMap),
      count3: buildPerPlayerRoundTotalsFrom(wrap(voteDataRemote.game_vote_data_count_3), teamMap),
      count4: buildPerPlayerRoundTotalsFrom(wrap(voteDataRemote.game_vote_data_count_4), teamMap),
      count5: buildPerPlayerRoundTotalsFrom(wrap(voteDataRemote.game_vote_data_count_5), teamMap),
    };
  }, [voteDataRemote, teamMap]);

  // Build dynamic per-player per-round totals averaged across only enabled datasets when selectedDataset === 'all'
  const ENABLED_AGG_TOTALS = useMemo(() => {
    if (selectedDataset !== 'all') return null;
    const keys = enabledKeys || [];
    if (!keys.length) return new Map();
    const sourceMaps = keys
      .map((k) => DATASET_TOTALS[k])
      .filter((m) => m && typeof m.get === 'function');
    const result = new Map();
    for (const p of players) {
      const lower = p.name.toLowerCase();
      const perRound = {};
      for (const r of ROUNDS) {
        let sum = 0;
        let denom = 0;
        for (const m of sourceMaps) {
          const rec = m.get(lower) || {};
          const v = Number(rec[String(r)] || 0);
          sum += isNaN(v) ? 0 : v;
          denom += 1;
        }
        perRound[String(r)] = denom > 0 ? (sum / denom) : 0;
      }
      result.set(lower, perRound);
    }
    return result;
  }, [DATASET_TOTALS, enabledKeys, players, selectedDataset]);

  // Current dataset map for per-round totals used throughout (enabled-averaged when 'all')
  const perPlayerRoundTotals = useMemo(() => {
    if (selectedDataset === 'all') {
      return ENABLED_AGG_TOTALS || new Map();
    }
    return DATASET_TOTALS[selectedDataset] || new Map();
  }, [ENABLED_AGG_TOTALS, DATASET_TOTALS, selectedDataset]);

  // Dynamic hot streak computation based on the currently selected dataset
  const HOT_STREAK_ROUNDS_DYNAMIC = useMemo(() => {
    const map = new Map();
    for (const p of players) {
      const key = p.name.toLowerCase();
      const perRound = perPlayerRoundTotals.get(key) || {};
      const hits = ROUNDS.map((r) => ((perRound[String(r)] || 0) >= 0.8 ? 1 : 0));
      const hotSet = new Set();
      let runLength = 0;
      for (let i = 0; i < hits.length; i++) {
        if (hits[i] === 1) {
          runLength += 1;
        } else {
          if (runLength >= 3) {
            for (let j = i - runLength; j < i; j++) hotSet.add(ROUNDS[j]);
          }
          runLength = 0;
        }
      }
      if (runLength >= 3) {
        for (let j = hits.length - runLength; j < hits.length; j++) hotSet.add(ROUNDS[j]);
      }
      map.set(key, hotSet);
    }
    return map;
  }, [perPlayerRoundTotals, players]);

  // Helpers to read round value and totals (defined before use)
  const getRoundValue = (playerName, round) => {
    const key = playerName.toLowerCase();
    const r = perPlayerRoundTotals.get(key) || {};
    const v = r[String(round)] || 0;
    return v;
  };

  const getRowTotal = (playerName, range = null) => {
    return ROUNDS.reduce((sum, r) => {
      if (range) {
        const from = range.from ?? 0;
        const to = range.to ?? 24;
        if (r < from || r > to) return sum;
      }
      return sum + getRoundValue(playerName, r);
    }, 0);
  };

  // (No per-source precompute here; summary table uses per-round aggregate logic only)

  // Search filtering
  const normalizedSearch = searchTerm.toLowerCase();
  const filteredPlayers = players.filter(
    (p) =>
      p.name.toLowerCase().includes(normalizedSearch) ||
      p.team.toLowerCase().includes(normalizedSearch)
  );

  // Build rows with dynamic totals and positions
  const rows = useMemo(() => {
    const base = filteredPlayers.map((player) => ({
      player,
      total: getRowTotal(player.name, (filterFrom !== null || filterTo !== null) ? { from: filterFrom, to: filterTo } : null),
      originalPosition: player.position,
    }));

    // When a range filter is active OR a specific dataset is selected
    // OR the enabled dataset subset has changed (not all 8), sort by total and assign positions
    const datasetChanged = enabledKeys.length !== 8;
    if ((filterFrom !== null || filterTo !== null) || selectedDataset !== 'all' || datasetChanged) {
      base.sort((a, b) => {
        if (b.total !== a.total) return b.total - a.total;
        return a.originalPosition - b.originalPosition; // tie-breaker
      });
      return base.map((row, idx) => ({ ...row, displayPosition: idx + 1 }));
    }

    // Reset: preserve original order and positions
    return base.map((row) => ({ ...row, displayPosition: row.originalPosition }));
  }, [filteredPlayers, filterFrom, filterTo, selectedDataset, enabledKeys]);

  // Global votes-based rank map over all players (independent of text search)
  const rankByName = useMemo(() => {
    const all = players.map((p) => ({
      name: p.name,
      total: getRowTotal(p.name, (filterFrom !== null || filterTo !== null) ? { from: filterFrom, to: filterTo } : null),
      originalPosition: p.position,
    }));
    all.sort((a, b) => {
      if (b.total !== a.total) return b.total - a.total;
      return a.originalPosition - b.originalPosition;
    });
    const map = new Map();
    for (let i = 0; i < all.length; i++) {
      map.set(all[i].name.toLowerCase(), i + 1);
    }
    return map;
  }, [players, filterFrom, filterTo, selectedDataset, enabledKeys]);

  // Sorting helpers for vote frequency columns (based on per-round values from PER_PLAYER_ROUND_TOTALS)
  const getVFCount = (playerName, type) => {
    const thr = {
      polledMin: freqThresholds?.polledMin ?? 0.7,
      threeMin: freqThresholds?.threeMin ?? 2.4,
      twoMin: freqThresholds?.twoMin ?? 1.4,
      twoMax: freqThresholds?.twoMax ?? 2.4,
      oneMin: freqThresholds?.oneMin ?? 0.7,
      oneMax: freqThresholds?.oneMax ?? 1.4,
    };
    const perRound = perPlayerRoundTotals.get(playerName.toLowerCase()) || {};
    const values = ROUNDS.map((r) => perRound[String(r)] || 0);
    const inRange = (val, min, max) => (val >= min) && (max === null || val < max);
    switch (type) {
      case 'gp':
        return values.filter((v) => v >= thr.polledMin).length;
      case '3v':
        return values.filter((v) => v >= thr.threeMin).length;
      case '2v':
        return values.filter((v) => inRange(v, thr.twoMin, thr.twoMax)).length;
      case '1v':
        return values.filter((v) => inRange(v, thr.oneMin, thr.oneMax)).length;
      default:
        return 0;
    }
  };

  const sortedRows = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      if (sortKey === 'votes') {
        if (b.total !== a.total) return b.total - a.total;
        return a.originalPosition - b.originalPosition;
      }
      if (sortKey === 'vf_gp') {
        const va = getVFCount(a.player.name, 'gp');
        const vb = getVFCount(b.player.name, 'gp');
        if (vb !== va) return vb - va;
        return a.originalPosition - b.originalPosition;
      }
      if (sortKey === 'vf_3v') {
        const va = getVFCount(a.player.name, '3v');
        const vb = getVFCount(b.player.name, '3v');
        if (vb !== va) return vb - va;
        return a.originalPosition - b.originalPosition;
      }
      if (sortKey === 'vf_2v') {
        const va = getVFCount(a.player.name, '2v');
        const vb = getVFCount(b.player.name, '2v');
        if (vb !== va) return vb - va;
        return a.originalPosition - b.originalPosition;
      }
      if (sortKey === 'vf_1v') {
        const va = getVFCount(a.player.name, '1v');
        const vb = getVFCount(b.player.name, '1v');
        if (vb !== va) return vb - va;
        return a.originalPosition - b.originalPosition;
      }
      // default: sort by total
      if (b.total !== a.total) return b.total - a.total;
      return a.originalPosition - b.originalPosition;
    });
    // If filtered OR a specific dataset is selected, re-number positions by current sort
    if ((filterFrom !== null || filterTo !== null) || selectedDataset !== 'all') {
      return arr.map((row, idx) => ({ ...row, displayPosition: idx + 1 }));
    }
    return arr.map((row) => ({ ...row, displayPosition: row.originalPosition }));
  }, [rows, sortKey, filterFrom, filterTo, selectedDataset]);

  const getPlayerImage = (playerName) => PLAYER_NAME_TO_IMG.get(playerName) || null;

  // Brand heat ramp for per-round vote values, which run 0..3. Sub-1 rounds stay
  // faint sky tints (fringe votes), then the green deepens as the value climbs
  // toward a 3-vote game, ending on a near-black forest green at 3.
  // The green bands step through distinct emerald shades rather than opacities of a
  // single shade, because a tint like emerald-500/60 renders as the same colour as
  // solid emerald-700 over this card and would make a 1.2 read as a 2.2. Only the
  // lightest band is tinted, held at 85% so it stays a shade rather than a slab.
  // Text flips from dark to light where the fill crosses into the dark greens; every
  // band clears 5:1 against its fill.
  const getCellHeat = (votes) => {
    if (!votes || votes <= 0) return { box: 'bg-slate-800/20', text: 'text-slate-600' };
    if (votes < 0.5) return { box: 'bg-sky-500/10 ring-1 ring-inset ring-sky-500/15', text: 'text-sky-400' };
    if (votes < 1) return { box: 'bg-sky-500/20 ring-1 ring-inset ring-sky-500/25', text: 'text-sky-300' };
    if (votes < 1.5) return { box: 'bg-emerald-500/85 ring-1 ring-inset ring-emerald-400/30', text: 'text-slate-950' };
    if (votes < 2) return { box: 'bg-emerald-700 ring-1 ring-inset ring-emerald-500/40', text: 'text-emerald-50' };
    if (votes < 2.5) return { box: 'bg-emerald-800 ring-1 ring-inset ring-emerald-500/40', text: 'text-emerald-50' };
    return { box: 'bg-emerald-900 ring-1 ring-inset ring-emerald-600/60', text: 'text-emerald-50' };
  };

  

  return (
    <div className="rounded-2xl border border-slate-800 bg-[#0a1120] overflow-hidden">
      {/* Mobile: simplified table (POS, PLAYER, VOTES) with expandable rows */}
      <div className="md:hidden">
        <div className="overflow-hidden">
          <table className="w-full table-fixed">
            <colgroup>
              <col className="w-12" />
              <col className="w-[58%]" />
              <col className="w-[30%]" />
            </colgroup>
            <thead>
              <tr className="bg-slate-800/20 border-b border-slate-800/80">
                <th className="w-12 text-center pl-2 pr-2 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em]">POS</th>
                <th className="w-[58%] text-center pl-2 pr-2 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em]">PLAYER</th>
                <th className="w-[30%] text-center pl-2 pr-4 py-2.5 text-[10px] font-mono font-semibold text-emerald-300 uppercase tracking-[0.12em]">VOTES</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map(({ player, total, displayPosition }) => {
                const img = getPlayerImage(player.name);
                const isExpanded = expandedMobileRows[player.name] ?? true;
                // Same rank source as the desktop table below.
                const globalPos = rankByName.get(player.name.toLowerCase()) || displayPosition;
                return (
                  <React.Fragment key={`m-${player.name}`}>
                    <tr className={`border-b border-slate-800/50 ${isExpanded ? 'bg-slate-800/30' : ''}`}>
                      <td className="w-12 pl-2 pr-2 py-2 align-top">
                        <span className={`inline-flex items-center justify-center min-w-[24px] h-[20px] px-1 rounded-md border text-[11px] font-mono font-semibold tabular-nums ${rankBadgeClass(globalPos)}`}>
                          {globalPos}
                        </span>
                      </td>
                      <td className="w-[58%] pl-2 pr-2 py-2 align-top">
                        <div className="flex items-center space-x-2 min-w-0">
                          {img ? (
                            <img src={img} alt={player.name} className="w-8 h-8 rounded-full object-cover flex-shrink-0 ring-1 ring-slate-700/60" />
                          ) : (
                            <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0 ring-1 ring-slate-700/60">
                              <span className="text-white font-bold text-[10px]">{player.image}</span>
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div
                              className="flex items-center gap-1.5 min-w-0 text-white font-semibold text-xs cursor-pointer hover:text-emerald-300"
                              onClick={() => {
                                setSelectedPlayer(player);
                                setIsModalOpen(true);
                              }}
                              title="View vote breakdown"
                            >
                              <span className="truncate">{player.name}</span>
                              <BreakdownIcon className="w-3 h-3 flex-shrink-0 text-emerald-400/60" />
                            </div>
                            <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-slate-500 truncate">{player.team}</div>
                          </div>
                        </div>
                      </td>
                      <td className="w-[30%] pl-2 pr-4 py-2 align-top text-right">
                        <div className="inline-flex items-center justify-end gap-2 pr-1">
                          <span className="text-emerald-300 font-mono font-bold tabular-nums text-sm">{total.toFixed(2)}</span>
                          <button
                            className={`w-7 h-7 flex items-center justify-center rounded-md border cursor-pointer transition-all duration-200 ${isExpanded ? 'rotate-180 border-emerald-500/40 bg-emerald-500/10 text-emerald-300' : 'border-slate-700/60 bg-slate-800/40 text-slate-400'}`}
                            aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                            onClick={() => toggleMobileRow(player.name)}
                          >
                            <ChevronDown className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-[#060b16] border-b border-slate-800/50">
                        <td colSpan={3} className="px-0 pb-0">
                          <div className="overflow-x-auto styled-scrollbar">
                            <table className="min-w-[720px] w-full">
                              <thead>
                                <tr className="bg-slate-800/20 border-b border-slate-800/60">
                                  {ROUNDS.map((r) => (
                                    <th key={`mh-${player.name}-${r}`} className="text-center px-2 py-2 text-[10px] font-mono font-medium text-slate-500 tabular-nums whitespace-nowrap">{r}</th>
                                  ))}
                                  {(filterFrom !== null || filterTo !== null) && selectedDataset === 'all' && (
                                    <th className="text-center px-2 py-2 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] whitespace-nowrap"><span className="inline-flex items-center justify-center">Implied Odds<ImpliedOddsInfo /></span></th>
                                  )}
                                  {voteFrequencyEnabled && (
                                    <>
                                      <th className="text-center px-2 py-2 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] whitespace-nowrap">3V</th>
                                      <th className="text-center px-2 py-2 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] whitespace-nowrap">2V</th>
                                      <th className="text-center px-2 py-2 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] whitespace-nowrap">1V</th>
                                      <th className="text-center px-2 py-2 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] whitespace-nowrap">GP</th>
                                    </>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  {ROUNDS.map((r) => {
                                    const dimmed = (filterFrom !== null && r < (filterFrom ?? 0)) || (filterTo !== null && r > (filterTo ?? 24));
                                    const hotSet = HOT_STREAK_ROUNDS_DYNAMIC.get(player.name.toLowerCase()) || new Set();
                                    const isHot = hotSet.has(r);
                                    const v = getRoundValue(player.name, r);
                                    return (
                                      <td key={`mv-${player.name}-${r}`} className="px-1 md:px-2 py-1.5 text-center">
                                        <div className={`flex flex-col items-center ${dimmed ? 'opacity-50' : ''}`}>
                                          <div className={`w-full text-center leading-none text-[10px] md:text-[11px] py-1.5 rounded-md ${getCellHeat(v).box}`}>
                                            <span className={`text-[10px] font-mono tabular-nums font-semibold leading-none tracking-tight ${getCellHeat(v).text}`}>{v.toFixed(2)}</span>
                                          </div>
                                          <div className="mt-1 h-3 flex items-center justify-center">
                                            {isHot ? (
                                              <HotStreakIcon className="w-2.5 h-2.5" />
                                            ) : null}
                                          </div>
                                        </div>
                                      </td>
                                    );
                                  })}
                                  {(filterFrom !== null || filterTo !== null) && selectedDataset === 'all' && (() => {
                                    let leaderHits = 0;
                                    const from = filterFrom ?? 0;
                                    const to = filterTo ?? 24;
                                    const allSources = [
                                      { k: 'byb', map: DATASET_TOTALS.byb },
                                      { k: 'espn', map: DATASET_TOTALS.espn },
                                      { k: 'betfair', map: DATASET_TOTALS.betfair },
                                      { k: 'count1', map: DATASET_TOTALS.count1 },
                                      { k: 'count2', map: DATASET_TOTALS.count2 },
                                      { k: 'count3', map: DATASET_TOTALS.count3 },
                                      { k: 'count4', map: DATASET_TOTALS.count4 },
                                      { k: 'count5', map: DATASET_TOTALS.count5 },
                                    ];
                                    const enabledSources = allSources.filter(({ k }) => enabledSet.has(k));
                                    const denom = enabledSources.length || 1;
                                    for (const { map: sourceTotals } of enabledSources) {
                                      const totals = new Map();
                                      for (const p of players) {
                                        const key = p.name.toLowerCase();
                                        let sum = 0;
                                        for (let r = from; r <= to; r++) {
                                          const perRound = sourceTotals.get(key) || {};
                                          sum += perRound[String(r)] || 0;
                                        }
                                        totals.set(key, sum);
                                      }
                                      // Dead-heat: a tied top total contributes 1/n, not a full win.
                                      const thisPlayerTotal = totals.get(player.name.toLowerCase()) || 0;
                                      leaderHits += deadHeatShare(thisPlayerTotal, totals.values());
                                    }
                                    const hitPct = (leaderHits / denom) * 100;
                                    if (hitPct === 0) {
                                      return (
                                        <td className="text-center px-2 py-2 text-[10px] text-slate-600 font-mono whitespace-nowrap">N/A</td>
                                      );
                                    }
                                    const odds = (1 / (hitPct / 100)).toFixed(2);
                                    return (
                                      <td className="text-center px-2 py-2 text-[11px] whitespace-nowrap">
                                        <div className="flex flex-col items-center justify-center gap-1">
                                          <span className="text-emerald-300 font-mono font-bold tabular-nums text-[11px] leading-none">{odds}</span>
                                          <span className="text-slate-500 text-[9px] font-mono tabular-nums">{formatWins(leaderHits)}/{denom} ({formatPct(hitPct)}%)</span>
                                        </div>
                                      </td>
                                    );
                                  })()}
                                  {voteFrequencyEnabled && (() => {
                                    const thr = {
                                      polledMin: freqThresholds?.polledMin ?? 0.7,
                                      threeMin: freqThresholds?.threeMin ?? 2.4,
                                      twoMin: freqThresholds?.twoMin ?? 1.4,
                                      twoMax: freqThresholds?.twoMax ?? 2.4,
                                      oneMin: freqThresholds?.oneMin ?? 0.7,
                                      oneMax: freqThresholds?.oneMax ?? 1.4,
                                    };
                                    const perRound = perPlayerRoundTotals.get(player.name.toLowerCase()) || {};
                                    const values = ROUNDS.map((r) => perRound[String(r)] || 0);
                                    const inRange = (val, min, max) => (val >= min) && (max === null || val < max);
                                    const countMatches = (predicate) => values.filter(predicate).length;
                                    const gamesPolled = countMatches((v) => v >= thr.polledMin);
                                    const threeVotes = countMatches((v) => v >= thr.threeMin);
                                    const twoVotes = countMatches((v) => inRange(v, thr.twoMin, thr.twoMax));
                                    const oneVotes = countMatches((v) => inRange(v, thr.oneMin, thr.oneMax));
                                    const renderAggPill = (val) => (
                                      <span className="inline-block text-emerald-300 font-semibold font-mono tabular-nums text-xs min-w-[2.5rem] text-center">{val}</span>
                                    );
                                    return (
                                      <>
                                        <td className="text-center px-2 py-2 text-[11px] whitespace-nowrap">{renderAggPill(threeVotes)}</td>
                                        <td className="text-center px-2 py-2 text-[11px] whitespace-nowrap">{renderAggPill(twoVotes)}</td>
                                        <td className="text-center px-2 py-2 text-[11px] whitespace-nowrap">{renderAggPill(oneVotes)}</td>
                                        <td className="text-center px-2 py-2 text-[11px] whitespace-nowrap">{renderAggPill(gamesPolled)}</td>
                                      </>
                                    );
                                  })()}
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block">
        <div className="overflow-x-auto styled-scrollbar">
          <table className="w-full md:min-w-[1000px]">
            <thead>
              <tr className="bg-slate-800/20 border-b border-slate-800/80">
                <th className={`${STICKY_HEAD} md:left-0 text-left px-2 md:px-4 py-2.5 w-[68px] text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em]`}>POS</th>
                <th className={`${STICKY_HEAD} md:left-[64px] text-left px-2 md:px-4 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] min-w-[200px] border-r border-slate-800/70`}>Player</th>
                {ROUNDS.map((r) => (
                  <th key={r} className="text-center px-1 md:px-2 py-2.5 text-[10px] font-mono font-medium text-slate-500 tabular-nums">
                    {r}
                  </th>
                ))}
                <th onClick={() => setSortKey && setSortKey('votes')} className="text-center px-2 md:px-4 py-2.5 text-[10px] font-mono font-semibold text-emerald-300 uppercase tracking-[0.12em] cursor-pointer select-none hover:text-emerald-200 transition-colors bg-emerald-500/10">Votes {sortKey === 'votes' && <SortCaret />}</th>
                {(filterFrom !== null || filterTo !== null) && selectedDataset === 'all' && (
                  <th className="text-center px-1 md:px-2 py-2.5 w-20 md:w-28 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em]"><span className="inline-flex items-center justify-center">Implied Odds<ImpliedOddsInfo /></span></th>
                )}
                {voteFrequencyEnabled && (
                  <>
                    <th
                      onClick={() => setSortKey && setSortKey('vf_3v')}
                      className="text-center px-1 md:px-2 py-2.5 w-12 md:w-16 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] cursor-pointer select-none hover:text-slate-300 transition-colors border-l border-slate-800/70"
                      title={`3V threshold: ≥ ${(freqThresholds?.threeMin ?? 2.4).toFixed(1)} votes`}
                    >
                      3V {sortKey === 'vf_3v' && <SortCaret />}
                    </th>
                    <th
                      onClick={() => setSortKey && setSortKey('vf_2v')}
                      className="text-center px-1 md:px-2 py-2.5 w-12 md:w-16 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] cursor-pointer select-none hover:text-slate-300 transition-colors"
                      title={`2V threshold: ≥ ${(freqThresholds?.twoMin ?? 1.4).toFixed(1)} and < ${(freqThresholds?.twoMax ?? 2.4).toFixed(1)}`}
                    >
                      2V {sortKey === 'vf_2v' && <SortCaret />}
                    </th>
                    <th
                      onClick={() => setSortKey && setSortKey('vf_1v')}
                      className="text-center px-1 md:px-2 py-2.5 w-12 md:w-16 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] cursor-pointer select-none hover:text-slate-300 transition-colors"
                      title={`1V threshold: ≥ ${(freqThresholds?.oneMin ?? 0.7).toFixed(1)} and < ${(freqThresholds?.oneMax ?? 1.4).toFixed(1)}`}
                    >
                      1V {sortKey === 'vf_1v' && <SortCaret />}
                    </th>
                    <th
                      onClick={() => setSortKey && setSortKey('vf_gp')}
                      className="text-center px-1 md:px-2 py-2.5 w-12 md:w-16 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] cursor-pointer select-none hover:text-slate-300 transition-colors"
                      title={`GP threshold: ≥ ${(freqThresholds?.polledMin ?? 0.7).toFixed(1)} votes`}
                    >
                      GP {sortKey === 'vf_gp' && <SortCaret />}
                    </th>
                  </>
                )}
                
              </tr>
            </thead>
            <tbody>
              {sortedRows.map(({ player, total, displayPosition }) => {
                const img = getPlayerImage(player.name);
                // Compute hit rate if a filter is active
                let leaderHits = 0;
                if (filterFrom !== null || filterTo !== null) {
                  const from = filterFrom ?? 0;
                  const to = filterTo ?? 24;
                  // For each source, find leaders within range
                  const allSources = [
                    { k: 'byb', map: DATASET_TOTALS.byb },
                    { k: 'espn', map: DATASET_TOTALS.espn },
                    { k: 'betfair', map: DATASET_TOTALS.betfair },
                    { k: 'count1', map: DATASET_TOTALS.count1 },
                    { k: 'count2', map: DATASET_TOTALS.count2 },
                    { k: 'count3', map: DATASET_TOTALS.count3 },
                    { k: 'count4', map: DATASET_TOTALS.count4 },
                    { k: 'count5', map: DATASET_TOTALS.count5 },
                  ];
                  const enabledSources = allSources.filter(({ k }) => enabledSet.has(k));
                  const denom = enabledSources.length || 1;
                  for (const { map: sourceTotals } of enabledSources) {
                    // Compute totals for all players in this source
                    const totals = new Map();
                    for (const p of players) {
                      const key = p.name.toLowerCase();
                      let sum = 0;
                      for (let r = from; r <= to; r++) {
                        const perRound = sourceTotals.get(key) || {};
                        sum += perRound[String(r)] || 0;
                      }
                      totals.set(key, sum);
                    }
                    // Dead-heat: a tied top total contributes 1/n, not a full win.
                    const thisPlayerTotal = totals.get(player.name.toLowerCase()) || 0;
                    leaderHits += deadHeatShare(thisPlayerTotal, totals.values());
                  }
                }
                const globalPos = rankByName.get(player.name.toLowerCase()) || displayPosition;
                return (
                  <tr key={player.position} className="group/row border-b border-slate-800/50 hover:bg-slate-800/40 transition-colors">
                    {/* Position */}
                    <td className={`${STICKY_CELL} md:left-0 px-2 md:px-4 py-1.5 w-[68px]`}>
                      <span className={`inline-flex items-center justify-center min-w-[26px] h-[22px] px-1.5 rounded-md border text-[11px] font-mono font-semibold tabular-nums ${rankBadgeClass(globalPos)}`}>
                        {globalPos}
                      </span>
                    </td>
                    {/* Player cell */}
                    <td className={`${STICKY_CELL} md:left-[64px] px-2 md:px-4 py-1.5 min-w-[200px] border-r border-slate-800/70`}>
                      <div className="flex items-center space-x-2 md:space-x-3">
                        {img ? (
                          <img src={img} alt={player.name} className="w-8 h-8 md:w-9 md:h-9 rounded-full object-cover flex-shrink-0 ring-1 ring-slate-700/60" />
                        ) : (
                          <div className="w-8 h-8 md:w-9 md:h-9 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0 ring-1 ring-slate-700/60">
                            <span className="text-white font-bold text-[10px]">{player.image}</span>
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div
                            className="flex items-center gap-1.5 min-w-0 text-white font-semibold text-xs md:text-[13px] cursor-pointer hover:text-emerald-300 transition-colors"
                            onClick={() => {
                              setSelectedPlayer(player);
                              setIsModalOpen(true);
                            }}
                            title="View vote breakdown"
                          >
                            <span className="truncate">{player.name}</span>
                            <BreakdownIcon className="w-3 h-3 flex-shrink-0 text-emerald-400/60" />
                          </div>
                          <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-slate-500 truncate">{player.team}</div>
                        </div>
                      </div>
                    </td>

                    {/* Round cells */}
                    {ROUNDS.map((r) => {
                      const dimmed = (filterFrom !== null && r < filterFrom) || (filterTo !== null && r > filterTo);
                      const hotSet = HOT_STREAK_ROUNDS_DYNAMIC.get(player.name.toLowerCase()) || new Set();
                      const isHot = hotSet.has(r);
                      const v = getRoundValue(player.name, r);
                      return (
                        <td key={`${player.name}-${r}`} className="px-1 md:px-2 py-1.5 text-center">
                          <div className={`flex flex-col items-center ${dimmed ? 'opacity-50' : ''}`}>
                            <div className={`w-full text-center leading-none text-[10px] md:text-[11px] py-1.5 rounded-md ${getCellHeat(v).box}`}>
                              <span className={`text-[11px] font-mono tabular-nums font-semibold leading-none tracking-tight ${getCellHeat(v).text}`}>{v.toFixed(2)}</span>
                            </div>
                            <div className="mt-0.5 h-3 flex items-center justify-center">
                              {isHot ? (
                                <HotStreakIcon className="w-2.5 h-2.5 md:w-3 md:h-3" />
                              ) : null}
                            </div>
                          </div>
                        </td>
                      );
                    })}

                    {/* Total */}
                    <td className="px-2 md:px-4 py-1.5 text-center bg-emerald-500/[0.06]">
                      <span className="text-emerald-300 font-mono font-bold tabular-nums text-sm md:text-[15px]">{total.toFixed(2)}</span>
                    </td>
                    {(filterFrom !== null || filterTo !== null) && selectedDataset === 'all' && (() => {
                      const denom = enabledKeys.length || 1;
                      const hitPct = (leaderHits / denom) * 100;
                      if (hitPct === 0) {
                        return (
                          <td className="px-1 md:px-2 py-1.5 text-center w-20 md:w-28">
                            <span className="text-slate-600 text-[11px] font-mono">N/A</span>
                          </td>
                        );
                      }
                      const odds = (1 / (hitPct / 100)).toFixed(2);
                      return (
                        <td className="px-1 md:px-2 py-1.5 text-center w-20 md:w-28">
                          <div className="flex flex-col items-center justify-center gap-1">
                            <span className="text-emerald-300 font-mono font-bold tabular-nums text-xs md:text-[13px] leading-none">{odds}</span>
                            <span className="text-slate-500 text-[10px] font-mono tabular-nums">{formatWins(leaderHits)}/{denom} ({formatPct(hitPct)}%)</span>
                          </div>
                        </td>
                      );
                    })()}
                    {voteFrequencyEnabled && (() => {
                      const thr = {
                        polledMin: freqThresholds?.polledMin ?? 0.7,
                        threeMin: freqThresholds?.threeMin ?? 2.4,
                        twoMin: freqThresholds?.twoMin ?? 1.4,
                        twoMax: freqThresholds?.twoMax ?? 2.4,
                        oneMin: freqThresholds?.oneMin ?? 0.7,
                        oneMax: freqThresholds?.oneMax ?? 1.4,
                      };
                      const perRound = perPlayerRoundTotals.get(player.name.toLowerCase()) || {};
                      const values = ROUNDS.map((r) => perRound[String(r)] || 0);
                      const inRange = (val, min, max) => (val >= min) && (max === null || val < max);
                      const countMatches = (predicate) => values.filter(predicate).length;
                      const gamesPolled = countMatches((v) => v >= thr.polledMin);
                      const threeVotes = countMatches((v) => v >= thr.threeMin);
                      const twoVotes = countMatches((v) => inRange(v, thr.twoMin, thr.twoMax));
                      const oneVotes = countMatches((v) => inRange(v, thr.oneMin, thr.oneMax));
                      const renderAggPill = (val) => (
                        <span className="inline-block text-emerald-300 font-semibold font-mono tabular-nums text-xs md:text-[13px] min-w-[2.5rem] text-center">{val}</span>
                      );
                      return (
                        <>
                          <td className="px-1 md:px-2 py-1.5 text-center border-l border-slate-800/70">{renderAggPill(threeVotes)}</td>
                          <td className="px-1 md:px-2 py-1.5 text-center">{renderAggPill(twoVotes)}</td>
                          <td className="px-1 md:px-2 py-1.5 text-center">{renderAggPill(oneVotes)}</td>
                          <td className="px-1 md:px-2 py-1.5 text-center">{renderAggPill(gamesPolled)}</td>
                        </>
                      );
                    })()}
                
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Player Vote Modal */}
      <DetailedPlayerVoteModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedPlayer(null);
        }}
        player={selectedPlayer}
        playerImage={selectedPlayer ? getPlayerImage(selectedPlayer.name) : null}
        gameVoteData={voteDataRemote || null}
      />
    </div>
  );
};

export default PlayerVoteSummaryTable;